
import { useCallback, useEffect, useRef, useState } from "react";
import { RentiiRailButtons, RentiiRailPanel } from "./RentiiDock";
/* Finds the first opaque background painted behind the rail and reports whether it is light. */
function isLightBehind(rail) {
    if (!rail)
        return false;
    const box = rail.getBoundingClientRect();
    const x = box.left + box.width / 2;
    const y = box.top + box.height / 2;
    for (const el of document.elementsFromPoint(x, y)) {
        if (rail.contains(el))
            continue;
        const match = getComputedStyle(el).backgroundColor.match(/[\d.]+/g);
        if (!match)
            continue;
        const [r, g, b, a = 1] = match.map(Number);
        if (a < 0.5)
            continue;
        return 0.2126 * r + 0.7152 * g + 0.0722 * b > 160;
    }
    const [r, g, b] = (getComputedStyle(document.body).backgroundColor.match(/[\d.]+/g) ?? ["0", "0", "0"]).map(Number);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 160;
}
/* Vercel-toolbar-style left rail with Ask Rentii + Rentid Shortcuts.
   Slides in once the rentDirect section is on screen. */
export function ProductRail() {
    const [visible, setVisible] = useState(false);
    const [panel, setPanel] = useState(null);
    const [onLight, setOnLight] = useState(false);
    const navRef = useRef(null);
    const closePanel = useCallback(() => setPanel(null), []);
    const togglePanel = (next) => setPanel((p) => (p === next ? null : next));
    useEffect(() => {
        if (!panel)
            return;
        const onPointerDown = (e) => {
            if (!navRef.current?.contains(e.target))
                setPanel(null);
        };
        document.addEventListener("pointerdown", onPointerDown);
        return () => document.removeEventListener("pointerdown", onPointerDown);
    }, [panel]);
    useEffect(() => {
        const rentDirect = document.getElementById("rent-direct");
        if (!rentDirect)
            return;
        const heading = rentDirect.querySelector("h2") ?? rentDirect;
        const hero = document.getElementById("home-hero");
        /* The rail waits until the rentDirect heading has fully scrolled above the rail
           and the hero is entirely off screen, so it never overlaps the hero. */
        let frame = 0;
        const update = () => {
            frame = 0;
            const railTop = navRef.current?.getBoundingClientRect().top ?? window.innerHeight / 2;
            const heroGone = !hero || hero.getBoundingClientRect().bottom <= 0;
            const show = heroGone && heading.getBoundingClientRect().bottom <= railTop;
            setVisible(show);
            if (!show)
                setPanel(null);
            setOnLight(isLightBehind(navRef.current));
        };
        const onScroll = () => {
            if (!frame)
                frame = requestAnimationFrame(update);
        };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, []);
    return (<nav ref={navRef} aria-label="Products" aria-hidden={!visible} inert={!visible} className={`fixed left-2 top-1/2 max-md:hidden z-40 -translate-y-1/2 transition-all duration-500 ease-out ${visible ? "translate-x-0 opacity-100" : "pointer-events-none -translate-x-6 opacity-0"}`}>
      <div className={`flex flex-col gap-1 rounded-full border border-white/10 p-1 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6),0_0_60px_-18px_rgba(51,247,190,0.35)] ring-1 ring-white/5 backdrop-blur-xl backdrop-saturate-150 transition-colors duration-300 ${onLight ? "bg-black" : "bg-black/65"}`}>
        <RentiiRailButtons panel={panel} onToggle={togglePanel} onLight={onLight}/>
      </div>

      <RentiiRailPanel panel={panel} onClose={closePanel}/>
    </nav>);
}
